import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X, AlertOctagon, ArrowLeft, ShieldAlert, ExternalLink } from 'lucide-react';
import QRCode from 'qrcode';
import { getPrintPortal, openCleanPrintWindow } from '../utils/printUtils';

export interface RejectedItemDetail {
  codigo?: string;
  nombre: string;
  cantidadRechazada: number;
  unidad?: string;
  motivo?: string;
}

interface PrintSheetRampRejectionProps {
  nriFolio?: string;
  actaRechazoFolio?: string;
  folioOAB: string;
  proveedor?: string;
  numeroNotaEntrega: string;
  fechaRecepcion: string;
  receptorNombre?: string;
  choferNombre?: string;
  placaVehiculo?: string;
  itemsRechazados: RejectedItemDetail[];
  onClose: () => void;
}

export const PrintSheetRampRejection: React.FC<PrintSheetRampRejectionProps> = ({
  nriFolio,
  actaRechazoFolio,
  folioOAB,
  proveedor,
  numeroNotaEntrega,
  fechaRecepcion,
  receptorNombre = 'Operador de Rampa',
  choferNombre = 'Chofer de Transporte',
  placaVehiculo = 'N/A',
  itemsRechazados,
  onClose
}) => {
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const printContentRef = useRef<HTMLDivElement>(null);

  const effectiveNriFolio = nriFolio || `NRI-${(fechaRecepcion || '').replace(/-/g, '')}-01`;
  const effectiveActaFolio = actaRechazoFolio || `RECHAZO-${effectiveNriFolio}`;

  useEffect(() => {
    const el = getPrintPortal();
    setPortalElement(el);

    const prevTitle = document.title;
    document.title = `Acta_Rechazo_${effectiveActaFolio}_${folioOAB}`;

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://sanesca-inventario.pages.dev';
    const targetUrl = `${origin}/?rampReceipt=${encodeURIComponent(effectiveNriFolio)}&folio=${encodeURIComponent(folioOAB)}`;
    QRCode.toDataURL(targetUrl, { width: 112, margin: 1, errorCorrectionLevel: 'M' })
      .then(url => setQrDataUrl(url))
      .catch(() => setQrDataUrl(''));

    return () => {
      document.title = prevTitle;
    };
  }, [effectiveActaFolio, effectiveNriFolio, folioOAB]);

  const handlePrint = () => {
    window.print();
  };

  const handleOpenCleanWindow = () => {
    if (!printContentRef.current) return;
    openCleanPrintWindow({
      title: `Acta_Rechazo_${effectiveActaFolio}_${folioOAB}`,
      contentHtml: printContentRef.current.innerHTML,
      pageSize: 'letter portrait',
      margin: '6mm 8mm'
    });
  };

  const totalRechazado = itemsRechazados.reduce((sum, it) => sum + (it.cantidadRechazada || 0), 0);

  const content = (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col items-center overflow-y-auto p-2 sm:p-4 md:py-6 font-sans text-slate-800 print-ramp-root print:static print:p-0 print:m-0 print:bg-white print:overflow-visible">
      <style>{`
        @media print {
          @page {
            size: letter portrait;
            margin: 6mm 8mm;
          }
          body {
            background: #ffffff !important;
            color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print {
            display: none !important;
          }
          .print-ramp-root {
            position: static !important;
            background: transparent !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
            height: auto !important;
          }
          .print-ramp-card {
            width: 100% !important;
            max-width: 100% !important;
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            background: #ffffff !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .avoid-break {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>

      {/* Controles superiores (no se imprimen) */}
      <div className="no-print w-full max-w-4xl bg-rose-950 text-white rounded-xl shadow-lg p-3 sm:p-4 mb-4 flex items-center justify-between border border-rose-900">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 hover:bg-rose-900 rounded-lg text-rose-300 hover:text-white transition"
            title="Volver"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-base font-bold flex items-center gap-2">
              <AlertOctagon className="w-5 h-5 text-rose-400" />
              Acta de No Conformidad y Rechazo en Rampa
            </h2>
            <p className="text-xs text-rose-200">
              Acta: <span className="font-mono text-white font-bold">{effectiveActaFolio}</span> · NRI: <span className="font-mono text-rose-300">{effectiveNriFolio}</span> · OAB: {folioOAB}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleOpenCleanWindow}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-rose-900/80 hover:bg-rose-900 text-rose-200 hover:text-white border border-rose-800 transition active:scale-95 shadow-md"
            title="Abrir en ventana limpia para tablets o navegadores con diálogo de impresión recortado"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="hidden sm:inline">Ventana Limpia</span>
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-lg shadow-sm transition active:scale-95 text-xs sm:text-sm"
          >
            <Printer className="w-4 h-4" />
            <span>Imprimir Acta (Carta)</span>
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-rose-900 rounded-lg text-rose-300 hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Hoja Física Carta */}
      <div 
        ref={printContentRef}
        className="w-full max-w-4xl bg-white border border-rose-300 rounded-lg p-6 sm:p-8 shadow-sm print-ramp-card print:p-0 print:border-0 print:shadow-none print:w-full"
      >
        {/* Cabecera Legal con QR */}
        <div className="border-b-2 border-rose-800 pb-3 mb-4 flex justify-between items-start gap-4 avoid-break">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-rose-700 text-white flex items-center justify-center font-bold text-lg rounded">
                !
              </div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">SANESCA PRO</h1>
            </div>
            <p className="text-xs text-rose-700 mt-1 uppercase tracking-wider font-bold">
              Acta de No Conformidad y Devolución Inmediata de Mercancía en Muelle
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Soporte Externo para Transportista, Proveedor y Reclamo de Compras — Vinculado a {effectiveNriFolio}
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-right">
              <span className="inline-block px-3 py-1 bg-rose-50 border border-rose-400 text-rose-800 font-mono text-sm font-black rounded">
                {effectiveActaFolio}
              </span>
              <p className="text-xs text-slate-700 mt-1 font-mono font-bold">
                NRI Asociada: {effectiveNriFolio}
              </p>
              <p className="text-[11px] text-slate-600 mt-0.5 font-medium">
                OAB: <span className="font-mono font-semibold">{folioOAB}</span> · Fecha: <span className="font-semibold text-slate-800">{fechaRecepcion}</span>
              </p>
            </div>
            {qrDataUrl && (
              <div className="border border-rose-200 rounded p-1 bg-white shrink-0 text-center">
                <img src={qrDataUrl} alt={`QR ${effectiveActaFolio}`} className="w-16 h-16 object-contain" />
                <span className="block text-[8px] font-mono text-rose-700 uppercase">Expediente NRI</span>
              </div>
            )}
          </div>
        </div>

        {/* Metadatos del Transporte */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-rose-50/50 border border-rose-200 rounded-lg text-xs mb-4 avoid-break">
          <div>
            <span className="text-slate-500 block uppercase font-medium text-[10px]">Proveedor:</span>
            <span className="font-bold text-slate-900 text-xs truncate block">{proveedor || 'Proveedor General'}</span>
          </div>
          <div>
            <span className="text-slate-500 block uppercase font-medium text-[10px]">Guía / Remisión:</span>
            <span className="font-bold text-slate-900 text-xs block font-mono text-rose-800">
              #{numeroNotaEntrega || 'S/N'}
            </span>
          </div>
          <div>
            <span className="text-slate-500 block uppercase font-medium text-[10px]">Chofer / Transportista:</span>
            <span className="font-bold text-slate-900 text-xs block">{choferNombre || 'Chofer de Transporte'}</span>
          </div>
          <div>
            <span className="text-slate-500 block uppercase font-medium text-[10px]">Placa Vehículo:</span>
            <span className="font-bold text-slate-900 text-xs block font-mono">{placaVehiculo || 'N/A'}</span>
          </div>
        </div>

        {/* Detalle de Mercancía Devuelta */}
        <div className="mb-4">
          <h3 className="text-xs font-bold uppercase text-slate-700 mb-2 tracking-wider avoid-break">
            Detalle de Renglones Devueltos en el Acto ({itemsRechazados.length} renglones · {totalRechazado} unidades):
          </h3>
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b-2 border-slate-800 bg-slate-100 text-slate-700 font-bold uppercase text-[10px] avoid-break">
                <th className="py-1.5 px-2 text-center w-8">#</th>
                <th className="py-1.5 px-2">Descripción del Insumo</th>
                <th className="py-1.5 px-2 text-center">Unidad</th>
                <th className="py-1.5 px-2 text-right text-rose-700">Cant. Rechazada</th>
                <th className="py-1.5 px-2 text-left">Motivo Técnico de No Conformidad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {itemsRechazados.map((it, idx) => (
                <tr key={idx} className="hover:bg-rose-50/30 avoid-break">
                  <td className="py-2 px-2 text-center font-mono text-slate-400 text-[11px]">{idx + 1}</td>
                  <td className="py-2 px-2">
                    <div className="font-bold text-slate-900 text-xs">{it.nombre}</div>
                    {it.codigo && <span className="font-mono text-[9px] text-slate-500">{it.codigo}</span>}
                  </td>
                  <td className="py-2 px-2 text-center text-slate-600 uppercase font-mono text-[10.5px]">{it.unidad || 'und'}</td>
                  <td className="py-2 px-2 text-right font-mono font-bold text-rose-700 bg-rose-50/50 text-[11px]">
                    {it.cantidadRechazada}
                  </td>
                  <td className="py-2 px-2 text-slate-800 font-medium text-xs">
                    {it.motivo || 'Defecto físico o discrepancia con orden de compra'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Declaración Legal de Custodia */}
        <div className="p-3 bg-slate-50 border border-slate-300 rounded text-[11px] text-slate-700 leading-relaxed mb-6 flex items-start gap-2.5 avoid-break">
          <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
          <div>
            <strong>Declaración de Devolución Física ({effectiveActaFolio}):</strong> Se deja constancia fehaciente de que las <strong>{totalRechazado} unidades</strong> descritas anteriormente <strong>NO fueron ingresadas al inventario de Sanesca PRO</strong> debido a las no conformidades detalladas, habiendo sido devueltas de manera inmediata al transporte del proveedor. El transportista firma al pie en señal de recepción de los bienes devueltos para su posterior reposición o emisión de la nota de crédito respectiva.
          </div>
        </div>

        {/* Firmas Mutuas */}
        <div className="grid grid-cols-2 gap-10 pt-4 border-t border-slate-300 text-xs avoid-break">
          <div className="text-center">
            <div className="border-b border-slate-400 h-12 mb-1.5"></div>
            <p className="font-bold text-slate-800 text-xs">Por: Sanesca PRO ({receptorNombre})</p>
            <p className="text-[9.5px] text-slate-500">Nombre, Firma y Cédula del Receptor de Rampa</p>
          </div>
          <div className="text-center">
            <div className="border-b border-slate-400 h-12 mb-1.5"></div>
            <p className="font-bold text-slate-800 text-xs">Recibí Conforme Devolución ({choferNombre || 'Transportista'})</p>
            <p className="text-[9.5px] text-slate-500">Nombre, Firma, Cédula y Teléfono del Chofer</p>
          </div>
        </div>
      </div>
    </div>
  );

  return portalElement ? createPortal(content, portalElement) : null;
};
